import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 90, right: 30, bottom: 60, left: 50 };
const LEGEND_HEIGHT = 14;
const LEGEND_WIDTH = 200;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '4px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  sortToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: '#6b7280',
  },
  sortButton: {
    background: 'none',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    padding: '3px 8px',
    fontSize: '11px',
    cursor: 'pointer',
    color: '#6b7280',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  sortButtonActive: {
    background: '#2563eb',
    border: '1px solid #2563eb',
    borderRadius: '4px',
    padding: '3px 8px',
    fontSize: '11px',
    cursor: 'pointer',
    color: '#fff',
    fontWeight: 600,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  scrollContainer: {
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '220px',
  },
};

export default function PriceVolatilityHeatmap({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [sortMode, setSortMode] = useState('name'); // 'name' | 'volatility'

  // Build a lookup map and compute total volatility per state for sorting
  const { cellMap, sortedStates } = useMemo(() => {
    if (!data || !data.cells || !data.states) return { cellMap: {}, sortedStates: [] };

    const map = {};
    const totalVolatility = {};
    data.cells.forEach((c) => {
      const key = `${c.state}|${c.date}`;
      map[key] = c;
      totalVolatility[c.state] = (totalVolatility[c.state] || 0) + (c.value || 0);
    });

    const states = [...data.states];
    if (sortMode === 'volatility') {
      states.sort((a, b) => (totalVolatility[b] || 0) - (totalVolatility[a] || 0));
    } else {
      states.sort((a, b) => a.localeCompare(b));
    }

    return { cellMap: map, sortedStates: states };
  }, [data, sortMode]);

  useEffect(() => {
    if (!data || !data.states || data.states.length === 0 || !data.dates || data.dates.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const stateCount = sortedStates.length;
    const dateCount = data.dates.length;

    // Calculate cell sizes to fit within a 700px-wide viewBox
    const gridWidth = 700 - MARGIN.left - MARGIN.right;
    const cellWidth = Math.max(Math.floor(gridWidth / dateCount), 12);
    const cellHeight = Math.max(Math.floor(cellWidth * 0.8), 14);

    const actualGridWidth = cellWidth * dateCount;
    const actualGridHeight = cellHeight * stateCount;

    const svgWidth = MARGIN.left + actualGridWidth + MARGIN.right;
    const svgHeight = MARGIN.top + actualGridHeight + MARGIN.bottom + LEGEND_HEIGHT + 20;

    svg
      .attr('width', '100%')
      .attr('viewBox', `0 0 ${svgWidth} ${svgHeight}`)
      .attr('preserveAspectRatio', 'xMinYMin meet');

    // Color scale: white -> yellow -> orange -> red
    const maxVal = data.maxValue || d3.max(data.cells, (c) => c.value) || 1;
    const colorScale = d3.scaleSequential()
      .domain([0, maxVal])
      .interpolator(d3.interpolateYlOrRd);

    const g = svg.append('g');

    // ---------------------------------------------------------------
    // Row highlight background rects (initially invisible)
    // ---------------------------------------------------------------
    const rowHighlights = g.selectAll('.row-highlight')
      .data(sortedStates)
      .enter()
      .append('rect')
      .attr('class', 'row-highlight')
      .attr('x', MARGIN.left - 2)
      .attr('y', (d, i) => MARGIN.top + i * cellHeight - 1)
      .attr('width', actualGridWidth + 4)
      .attr('height', cellHeight + 2)
      .attr('fill', 'rgba(37, 99, 235, 0.04)')
      .attr('rx', 2)
      .style('opacity', 0)
      .style('pointer-events', 'none');

    // Column highlight background rects (initially invisible)
    const colHighlights = g.selectAll('.col-highlight')
      .data(data.dates)
      .enter()
      .append('rect')
      .attr('class', 'col-highlight')
      .attr('x', (d, i) => MARGIN.left + i * cellWidth - 1)
      .attr('y', MARGIN.top - 2)
      .attr('width', cellWidth + 2)
      .attr('height', actualGridHeight + 4)
      .attr('fill', 'rgba(37, 99, 235, 0.04)')
      .attr('rx', 2)
      .style('opacity', 0)
      .style('pointer-events', 'none');

    // ---------------------------------------------------------------
    // Date labels (x-axis, rotated)
    // ---------------------------------------------------------------
    g.selectAll('.date-label')
      .data(data.dates)
      .enter()
      .append('text')
      .attr('class', 'date-label')
      .attr('x', (d, i) => MARGIN.left + i * cellWidth + cellWidth / 2)
      .attr('y', MARGIN.top - 8)
      .attr('text-anchor', 'start')
      .attr('transform', (d, i) => {
        const x = MARGIN.left + i * cellWidth + cellWidth / 2;
        const y = MARGIN.top - 8;
        return `rotate(-45, ${x}, ${y})`;
      })
      .text((d) => {
        const date = new Date(d + 'T00:00:00');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${date.getDate()}`;
      })
      .style('font-size', '9px')
      .style('fill', '#6b7280');

    // ---------------------------------------------------------------
    // State labels (y-axis)
    // ---------------------------------------------------------------
    g.selectAll('.state-label')
      .data(sortedStates)
      .enter()
      .append('text')
      .attr('class', 'state-label')
      .attr('x', MARGIN.left - 6)
      .attr('y', (d, i) => MARGIN.top + i * cellHeight + cellHeight / 2)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .style('font-size', '10px')
      .style('font-weight', '600')
      .style('fill', '#374151')
      .text((d) => d);

    // ---------------------------------------------------------------
    // Heatmap cells
    // ---------------------------------------------------------------
    for (let r = 0; r < stateCount; r++) {
      for (let c = 0; c < dateCount; c++) {
        const state = sortedStates[r];
        const date = data.dates[c];
        const key = `${state}|${date}`;
        const cell = cellMap[key];
        const val = cell ? cell.value : 0;

        g.append('rect')
          .attr('x', MARGIN.left + c * cellWidth)
          .attr('y', MARGIN.top + r * cellHeight)
          .attr('width', cellWidth - 1)
          .attr('height', cellHeight - 1)
          .attr('fill', val === 0 ? '#fafafa' : colorScale(val))
          .attr('stroke', '#fff')
          .attr('stroke-width', 1)
          .attr('rx', 1)
          .style('cursor', 'pointer')
          .on('mousemove', (event) => {
            // Highlight row and column
            rowHighlights.style('opacity', (d, i) => i === r ? 1 : 0);
            colHighlights.style('opacity', (d, i) => i === c ? 1 : 0);

            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              state,
              date,
              value: val,
              changePercent: cell ? cell.changePercent : 0,
            });
          })
          .on('mouseleave', () => {
            rowHighlights.style('opacity', 0);
            colHighlights.style('opacity', 0);
            setTooltip(null);
          });
      }
    }

    // ---------------------------------------------------------------
    // Color scale legend bar
    // ---------------------------------------------------------------
    const legendX = MARGIN.left;
    const legendY = MARGIN.top + actualGridHeight + 24;

    // Gradient definition
    const defs = svg.append('defs');
    const gradientId = 'volatility-heatmap-gradient';
    const gradient = defs.append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '0%');

    const numStops = 10;
    for (let i = 0; i <= numStops; i++) {
      const t = i / numStops;
      gradient.append('stop')
        .attr('offset', `${t * 100}%`)
        .attr('stop-color', colorScale(t * maxVal));
    }

    // Legend bar rect
    g.append('rect')
      .attr('x', legendX)
      .attr('y', legendY)
      .attr('width', LEGEND_WIDTH)
      .attr('height', LEGEND_HEIGHT)
      .attr('rx', 3)
      .style('fill', `url(#${gradientId})`);

    // Legend labels
    g.append('text')
      .attr('x', legendX)
      .attr('y', legendY + LEGEND_HEIGHT + 12)
      .attr('text-anchor', 'start')
      .style('font-size', '9px')
      .style('fill', '#9ca3af')
      .text('0%');

    g.append('text')
      .attr('x', legendX + LEGEND_WIDTH / 2)
      .attr('y', legendY + LEGEND_HEIGHT + 12)
      .attr('text-anchor', 'middle')
      .style('font-size', '9px')
      .style('fill', '#9ca3af')
      .text(`${(maxVal / 2).toFixed(1)}%`);

    g.append('text')
      .attr('x', legendX + LEGEND_WIDTH)
      .attr('y', legendY + LEGEND_HEIGHT + 12)
      .attr('text-anchor', 'end')
      .style('font-size', '9px')
      .style('fill', '#9ca3af')
      .text(`${maxVal.toFixed(1)}%`);

    // Legend title
    g.append('text')
      .attr('x', legendX + LEGEND_WIDTH + 10)
      .attr('y', legendY + LEGEND_HEIGHT / 2)
      .attr('dominant-baseline', 'middle')
      .style('font-size', '10px')
      .style('fill', '#6b7280')
      .text('Absolute price change');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, sortedStates, cellMap]);

  if (!data || !data.states || data.states.length === 0 || !data.dates || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Volatility Heatmap</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.titleRow}>
        <div>
          <div style={styles.title}>Price Volatility Heatmap</div>
          <div style={styles.subtitle}>Absolute price change magnitude by state and date</div>
        </div>
        <div style={styles.sortToggle}>
          <span>Sort by:</span>
          <button
            style={sortMode === 'name' ? styles.sortButtonActive : styles.sortButton}
            onClick={() => setSortMode('name')}
          >
            Name
          </button>
          <button
            style={sortMode === 'volatility' ? styles.sortButtonActive : styles.sortButton}
            onClick={() => setSortMode('volatility')}
          >
            Volatility
          </button>
        </div>
      </div>
      <div style={styles.scrollContainer}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '2px', color: '#1f2937' }}>
            {tooltip.state}
          </div>
          <div style={{ color: '#6b7280', marginBottom: '2px' }}>
            {tooltip.date}
          </div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Change: </span>
            <span
              style={{
                fontWeight: 600,
                color: tooltip.changePercent > 0 ? '#dc2626' : tooltip.changePercent < 0 ? '#16a34a' : '#6b7280',
              }}
            >
              {tooltip.changePercent === 0
                ? 'No change'
                : `${tooltip.changePercent > 0 ? '+' : ''}${tooltip.changePercent.toFixed(2)}%`}
            </span>
          </div>
          <div>
            <span style={{ color: '#6b7280' }}>Magnitude: </span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {tooltip.value.toFixed(2)}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
